import React, { Component } from "react";
import axios from "axios";
import { Helmet } from "react-helmet";
import styled, { createGlobalStyle } from "styled-components";
import ReactGA from "react-ga4";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const GlobalStyle = createGlobalStyle`
  html, body, #root {
    margin: 0;
    padding: 0;
    min-height: 100%;
    width: 100%;
  }

  body {
    overflow-x: hidden;
    overflow-y: auto;
    background: linear-gradient(135deg, #232d3d 0%, #2c374a 50%, #1b2230 100%);
    font-family: Arial, sans-serif;
  }

  .leaflet-container {
    font-family: Arial, sans-serif;
    background: #0f1720;
  }

  .leaflet-control-attribution {
    font-size: 10px;
  }

  .leaflet-popup-content-wrapper,
  .leaflet-popup-tip {
    background: #111827;
    color: #fff;
  }
`;

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 100vh;
  width: 100%;
  background: linear-gradient(135deg, #232d3d 0%, #2c374a 50%, #1b2230 100%);
  overflow-x: hidden;
  overflow-y: auto;
`;

const CenteredContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  max-width: 860px;
  padding: 20px 0 40px 0;
  box-sizing: border-box;
`;

const Card = styled.div`
  background-color: rgba(0, 0, 0, 0.7);
  border-radius: 14px;
  padding: 20px;
  margin: 20px;
  width: calc(100% - 40px);
  max-width: 680px;
  color: white;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.28);
  box-sizing: border-box;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: bold;
  margin: 0 0 10px 0;
  text-align: center;
`;

const Subtitle = styled.div`
  font-size: 14px;
  opacity: 0.9;
  text-align: center;
  margin-bottom: 14px;
`;

const Hero = styled.div`
  position: relative;
  width: 100%;
  min-height: 240px;
  border-radius: 12px;
  overflow: hidden;
  background: linear-gradient(135deg, #0f1720 0%, #1c2737 100%);
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background:
    radial-gradient(
      circle at top right,
      rgba(0, 125, 244, 0.2),
      transparent 35%
    ),
    linear-gradient(180deg, rgba(0, 0, 0, 0.05) 0%, rgba(0, 0, 0, 0.45) 100%);
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  padding: 18px;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
`;

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
  flex-wrap: wrap;
`;

const RunnerBox = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Avatar = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 999px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.08);
  border: 2px solid rgba(255, 255, 255, 0.12);
  flex-shrink: 0;
`;

const AvatarImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const AvatarFallback = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  color: #fff;
  background: rgba(0, 125, 244, 0.35);
`;

const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: ${(props) =>
    props.live ? "rgba(0, 200, 120, 0.18)" : "rgba(255,255,255,0.10)"};
  color: #fff;
  border: 1px solid
    ${(props) =>
      props.live ? "rgba(0, 200, 120, 0.35)" : "rgba(255,255,255,0.14)"};
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 700;
`;

const Dot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: ${(props) => (props.live ? "#00d17d" : "#c7d2de")};
  display: inline-block;
`;

const HeroMapWrap = styled.div`
  width: 100%;
  height: 260px;
  margin-top: 16px;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: #0f1720;

  @media (max-width: 640px) {
    height: 240px;
  }
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-top: 16px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const MetricCard = styled.div`
  background: rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 14px;
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

const MetricLabel = styled.div`
  font-size: 12px;
  opacity: 0.8;
  margin-bottom: 6px;
`;

const MetricValue = styled.div`
  font-size: 24px;
  font-weight: 800;
`;

const Description = styled.p`
  font-size: 15px;
  line-height: 1.6;
  margin: 18px 0 10px 0;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: break-word;
`;

const Button = styled.button`
  background-color: ${(props) => (props.secondary ? "transparent" : "#007df4")};
  color: white;
  border: ${(props) => (props.secondary ? "1px solid #1d9bf0" : "none")};
  border-radius: 10px;
  padding: 13px 16px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  width: 100%;

  &:hover {
    background-color: ${(props) =>
      props.secondary ? "rgba(29,155,240,0.08)" : "#0068cc"};
  }
`;

const Section = styled.div`
  margin-top: 18px;
`;

const SimpleMapBox = styled.div`
  width: 100%;
  min-height: 220px;
  border-radius: 12px;
  background:
    linear-gradient(135deg, rgba(0, 125, 244, 0.1), rgba(255, 255, 255, 0.04)),
    rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 18px;
  box-sizing: border-box;
`;

const SmallText = styled.div`
  font-size: 12px;
  opacity: 0.82;
`;

class LiveRouteMap extends Component {
  constructor(props) {
    super(props);
    this.mapRef = React.createRef();
    this.map = null;
    this.tileLayer = null;
    this.routeLine = null;
    this.liveMarker = null;
    this.hasFitOnce = false;
    this.userMovedMap = false;
  }

  componentDidMount() {
    this.initMap();
    this.renderRoute();
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.points !== this.props.points ||
      prevProps.lastPoint !== this.props.lastPoint ||
      prevProps.isLive !== this.props.isLive
    ) {
      this.renderRoute();
    }
  }

  componentWillUnmount() {
    if (this.map) {
      this.map.off("dragstart", this.handleUserMovedMap);
      this.map.off("zoomstart", this.handleUserMovedMap);
      this.map.remove();
      this.map = null;
    }
  }

  handleUserMovedMap = () => {
    this.userMovedMap = true;
  };

  initMap = () => {
    if (!this.mapRef.current || this.map) return;

    this.map = L.map(this.mapRef.current, {
      zoomControl: true,
      attributionControl: true,
    }).setView([31.7683, 35.2137], 13);

    this.tileLayer = L.tileLayer(
      "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      {
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      },
    );

    this.tileLayer.addTo(this.map);
    this.map.on("dragstart", this.handleUserMovedMap);
    this.map.on("zoomstart", this.handleUserMovedMap);
  };

  getLatLngs = () => {
    const { points = [] } = this.props;

    return points
      .filter(
        (p) =>
          p &&
          typeof p.lat === "number" &&
          typeof p.lng === "number" &&
          !Number.isNaN(p.lat) &&
          !Number.isNaN(p.lng),
      )
      .map((p) => [p.lat, p.lng]);
  };

  renderRoute = () => {
    if (!this.map) return;

    const latLngs = this.getLatLngs();

    if (this.routeLine) {
      this.map.removeLayer(this.routeLine);
      this.routeLine = null;
    }

    if (this.liveMarker) {
      this.map.removeLayer(this.liveMarker);
      this.liveMarker = null;
    }

    if (!latLngs.length) return;

    this.routeLine = L.polyline(latLngs, {
      color: "#007df4",
      weight: 5,
      opacity: 0.95,
      lineCap: "round",
      lineJoin: "round",
    }).addTo(this.map);

    const { lastPoint, isLive } = this.props;

    const finalPoint =
      lastPoint &&
      typeof lastPoint.lat === "number" &&
      typeof lastPoint.lng === "number"
        ? [lastPoint.lat, lastPoint.lng]
        : latLngs[latLngs.length - 1];

    this.liveMarker = L.circleMarker(finalPoint, {
      radius: 8,
      weight: 3,
      color: "#ffffff",
      fillColor: isLive ? "#00d17d" : "#007df4",
      fillOpacity: 1,
    }).addTo(this.map);

    this.liveMarker.bindPopup(isLive ? "Live position" : "Final position");

    if (!this.hasFitOnce) {
      if (latLngs.length === 1) {
        this.map.setView(latLngs[0], 15);
      } else {
        this.map.fitBounds(this.routeLine.getBounds(), {
          padding: [30, 30],
        });
      }
      this.hasFitOnce = true;
      return;
    }

    if (isLive && !this.userMovedMap) {
      this.map.panTo(finalPoint, { animate: true });
    }
  };

  render() {
    return <HeroMapWrap ref={this.mapRef} />;
  }
}

class LiveShare extends Component {
  constructor(props) {
    super(props);

    const shareToken =
      this.props.match?.params?.shareToken ||
      window.location.pathname.split("/live/")[1] ||
      "";

    this.state = {
      shareToken,
      loading: true,
      error: null,
      liveRun: null,
    };

    this.pollTimer = null;
  }

  componentDidMount() {
    ReactGA.send({
      hitType: "pageview",
      page: window.location.pathname + window.location.search,
    });

    this.fetchLiveRun();
  }

  componentWillUnmount() {
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
  }

  fetchLiveRun = () => {
    const { shareToken } = this.state;

    axios
      .get(
        `${process.env.REACT_APP_API_URL}users/live-share/public/${shareToken}`,
      )
      .then((response) => {
        const liveRun = response.data;

        if (this.pollTimer) {
          clearTimeout(this.pollTimer);
          this.pollTimer = null;
        }

        this.setState({
          liveRun,
          loading: false,
          error: null,
        });

        if (liveRun?.isLive) {
          this.pollTimer = setTimeout(this.fetchLiveRun, 8000);
        }
      })
      .catch((error) => {
        console.error("Error fetching live share:", error);
        this.setState({
          error: "Failed to load live activity",
          loading: false,
        });
      });
  };

  handleOpenInApp = () => {
    const { shareToken } = this.state;

    ReactGA.event({
      category: "Live Share",
      action: "Open In App Click",
      label: shareToken,
    });

    const deepLink = `bealyo-app://live/${shareToken}`;
    const playStore =
      "https://play.google.com/store/apps/details?id=com.bealyo.bealyoapp";

    window.location.href = deepLink;

    setTimeout(() => {
      window.location.href = playStore;
    }, 1500);
  };

  formatDuration = (startedAt, endedAt, isLive) => {
    if (!startedAt) return "--";

    const start = new Date(startedAt).getTime();
    const end = endedAt
      ? new Date(endedAt).getTime()
      : isLive
        ? Date.now()
        : Date.now();

    const totalSeconds = Math.max(0, Math.floor((end - start) / 1000));
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;

    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${s}s`;
    return `${s}s`;
  };

  formatStartedAt = (startedAt) => {
    if (!startedAt) return "";
    return new Date(startedAt).toLocaleString();
  };

  getOgImage = () => {
    const { liveRun } = this.state;
    if (liveRun?.user?.img_url) return liveRun.user.img_url;
    return "https://www.bealyo.com/images/prebealyoshare.png";
  };

  render() {
    const { loading, error, liveRun, shareToken } = this.state;

    const shareUrl = `https://www.bealyo.com/live/${shareToken}`;
    const ogTitle = liveRun?.title
      ? `${liveRun.title} | Live on Bealyo`
      : "Live Activity | Bealyo";
    const ogDescription = liveRun
      ? `${liveRun.user?.nickname || "Someone"} is ${liveRun.isLive ? "sharing a live activity" : "sharing a completed activity"} on Bealyo.`
      : "Follow a live activity on Bealyo.";
    const distanceKm =
      typeof liveRun?.totalDistanceKm === "number"
        ? liveRun.totalDistanceKm.toFixed(2)
        : "0.00";
    const pointCount = Array.isArray(liveRun?.points)
      ? liveRun.points.length
      : 0;
    const hasPoints =
      Array.isArray(liveRun?.points) && liveRun.points.length > 0;

    return (
      <PageContainer>
        <Helmet>
          <title>{ogTitle}</title>
          <meta name="description" content={ogDescription} />

          <meta property="al:android:package" content="com.bealyo.bealyoapp" />
          <meta
            property="al:android:url"
            content={`bealyo-app://live/${shareToken}`}
          />
          <meta property="al:android:app_name" content="Bealyo" />

          <meta property="og:type" content="website" />
          <meta property="og:url" content={shareUrl} />
          <meta property="og:title" content={ogTitle} />
          <meta property="og:description" content={ogDescription} />
          <meta property="og:image" content={this.getOgImage()} />

          <meta property="twitter:card" content="summary_large_image" />
          <meta property="twitter:url" content={shareUrl} />
          <meta property="twitter:title" content={ogTitle} />
          <meta property="twitter:description" content={ogDescription} />
          <meta property="twitter:image" content={this.getOgImage()} />
        </Helmet>

        <GlobalStyle />

        <CenteredContainer>
          <Card>
            {loading ? (
              <div style={{ textAlign: "center", padding: "30px 0" }}>
                Loading live activity...
              </div>
            ) : error ? (
              <div style={{ textAlign: "center", padding: "30px 0" }}>
                {error}
              </div>
            ) : (
              <>
                <Title>{liveRun?.title || "Live Activity"}</Title>
                <Subtitle>Shared through Bealyo</Subtitle>

                <Hero>
                  <HeroOverlay />
                  <HeroContent>
                    <TopRow>
                      <RunnerBox>
                        {/** 
                        <Avatar>
                          {liveRun?.user?.img_url ? (
                            <AvatarImg
                              src={liveRun.user.img_url}
                              alt={liveRun.user?.name || "Runner"}
                            />
                          ) : (
                            <AvatarFallback>
                              {(liveRun?.user?.name || "B")
                                .charAt(0)
                                .toUpperCase()}
                            </AvatarFallback>
                          )}
                        </Avatar>
                         */}
                        <div>
                          <div style={{ fontSize: "18px", fontWeight: 800 }}>
                            {liveRun?.user?.nickname || "Bealyo Member"}
                          </div>
                          <SmallText>
                            Started {this.formatStartedAt(liveRun?.startedAt)}
                          </SmallText>
                        </div>
                      </RunnerBox>

                      <StatusPill live={liveRun?.isLive}>
                        <Dot live={liveRun?.isLive} />
                        {liveRun?.isLive ? "Live now" : "Finished"}
                      </StatusPill>
                    </TopRow>

                    <div style={{ marginTop: 16 }}>
                      {hasPoints ? (
                        <LiveRouteMap
                          points={liveRun.points}
                          lastPoint={liveRun.lastPoint}
                          isLive={liveRun.isLive}
                        />
                      ) : (
                        <SimpleMapBox style={{ minHeight: 220, marginTop: 0 }}>
                          <div>
                            <div
                              style={{
                                fontWeight: 800,
                                fontSize: "18px",
                                marginBottom: 8,
                              }}
                            >
                              Waiting for live route
                            </div>
                            <SmallText>
                              Once the first points arrive, the live route will
                              appear here.
                            </SmallText>
                          </div>
                        </SimpleMapBox>
                      )}
                    </div>

                    <MetricsGrid>
                      <MetricCard>
                        <MetricLabel>Distance</MetricLabel>
                        <MetricValue>{distanceKm} km</MetricValue>
                      </MetricCard>

                      <MetricCard>
                        <MetricLabel>Duration</MetricLabel>
                        <MetricValue>
                          {this.formatDuration(
                            liveRun?.startedAt,
                            liveRun?.endedAt,
                            liveRun?.isLive,
                          )}
                        </MetricValue>
                      </MetricCard>

                      <MetricCard>
                        <MetricLabel>Points received</MetricLabel>
                        <MetricValue>{pointCount}</MetricValue>
                      </MetricCard>
                    </MetricsGrid>
                  </HeroContent>
                </Hero>

                <Description>
                  {liveRun?.isLive
                    ? "This activity is currently being shared live. Open it in the app for the full Bealyo experience."
                    : "This live activity has ended. You can still open it in Bealyo and view the session."}
                </Description>

                <Section>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                    }}
                  >
                    <span
                      style={{
                        alignSelf: "flex-start",
                        background: "#1d9bf0",
                        color: "#fff",
                        borderRadius: "999px",
                        padding: "4px 10px",
                        fontSize: "12px",
                        fontWeight: 500,
                      }}
                    >
                      Android Open Testing
                    </span>

                    <Button
                      onClick={() =>
                        window.open(
                          "https://play.google.com/store/apps/details?id=com.bealyo.bealyoapp",
                          "_blank",
                        )
                      }
                    >
                      Get Bealyo
                    </Button>

                    <Button secondary onClick={this.handleOpenInApp}>
                      Open in App
                    </Button>

                    <SmallText>
                      Available on Android for now. iOS later.
                    </SmallText>
                  </div>
                </Section>
              </>
            )}
          </Card>
        </CenteredContainer>
      </PageContainer>
    );
  }
}

export default LiveShare;
